import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import {
  useGetGrowthWheelEditorSummariesQuery,
  useGetGrowthWheelEditorStructureQuery,
  useGetGrowthWheelVersionsQuery,
} from '../../redux/growth-wheel.endpoints'
import { setIsReadOnly } from '../../redux/growthWheel.slice'
import { WheelManagement } from '../../components/GrowthWheelComponents/WheelManagement'
import { WheelVersionBar } from '../../components/GrowthWheelComponents/WheelVersionBar'
import type {
  Competency,
  SubCompetency,
} from '../../types/GrowthWheelManagement'
import type { GrowthWheelEditorStructure } from '../../types/GrowthWheel'
import Loader from '../../components/Loader/Loader'

function mapStructureToViewModel(data: GrowthWheelEditorStructure): {
  competencies: Competency[]
  subCompetencies: SubCompetency[]
} {
  const rawCompetencies = data.competencies ?? []
  const competencies: Competency[] = rawCompetencies.map(c => ({
    id: String(c.id),
    name: c.name,
    description: c.description,
  }))
  const subCompetencies: SubCompetency[] = rawCompetencies.flatMap(c =>
    c.subCompetencies.map(s => ({
      id: String(s.id),
      name: s.name,
      competency: String(c.id),
      description: s.description,
      levelDescriptions: s.levelDescriptions || {
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
      },
    })),
  )
  return { competencies, subCompetencies }
}

const GrowthWheelEditorDetail = () => {
  const dispatch = useDispatch()
  const { growthWheelId: growthWheelIdParam } = useParams<{
    growthWheelId: string
  }>()
  const growthWheelId = Number(growthWheelIdParam)
  const skip = !growthWheelIdParam || Number.isNaN(growthWheelId)

  useEffect(() => {
    return () => {
      dispatch(setIsReadOnly(false))
    }
  }, [dispatch])

  const { data: wheels } = useGetGrowthWheelEditorSummariesQuery()
  const wheelSummary = wheels?.find(w => w.id === growthWheelId)

  const {
    data: structure,
    isLoading,
    isError,
    refetch,
  } = useGetGrowthWheelEditorStructureQuery({ growthWheelId }, { skip })

  const { data: versions = [], isLoading: versionsLoading } =
    useGetGrowthWheelVersionsQuery({ growthWheelId }, { skip })

  const viewModel = useMemo(
    () => (structure ? mapStructureToViewModel(structure) : null),
    [structure],
  )
  const editingGuildName =
    structure?.growthWheel.guildName ?? wheelSummary?.guildName ?? null

  useEffect(() => {
    dispatch(setIsReadOnly(structure?.growthWheel.status === 'archived'))
  }, [dispatch, structure?.growthWheel.status])

  return (
    <div className="gw-editor-landing">
      <div className="gw-editor-detail-nav">
        <Link to="/edit-growth-wheel" className="gw-competency-back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to guilds
        </Link>
        {editingGuildName ? (
          <p className="gw-editor-detail-nav__editing">
            Editing:{' '}
            <span className="gw-editor-detail-nav__editing-name">
              {editingGuildName}
            </span>
          </p>
        ) : null}
      </div>

      {isLoading && <Loader />}
      {isError && <ErrorState onRetry={() => refetch()} />}
      {!skip && !isLoading && !isError && viewModel && (
        <WheelManagement
          growthWheelId={growthWheelId}
          competencies={viewModel.competencies}
          subCompetencies={viewModel.subCompetencies}
          guildName={structure?.growthWheel.guildName ?? wheelSummary?.guildName}
          topSlot={
            !versionsLoading && versions.length > 0 ? (
              <WheelVersionBar
                guildName={editingGuildName ?? undefined}
                versions={versions}
                selectedVersionId={growthWheelId}
              />
            ) : null
          }
        />
      )}
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div>
      <p className="text-danger">Could not load this growth wheel.</p>
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  )
}

export default GrowthWheelEditorDetail
